/**
 * Cross-window demo sync for /call?demo=1 — BroadcastChannel, localStorage, and relay fallback.
 * Does not touch production websocket / call API state.
 */

import type { TraversalDemoRoomIds, TraversalDemoSealResult } from "./callTraversalDemo";

export const DEMO_ROOM_DEFAULT = "xo-demo-room";

export type CallDemoSyncEventType =
  | "room_started"
  | "agreement_updated"
  | "note_added"
  | "moment_remembered"
  | "continuity_sealed"
  | "resume_editing";

export type CallDemoSyncPhase = "idle" | "ready" | "active" | "sealed";

export type CallDemoRememberedMoment = {
  id: string;
  at: number;
  snapshotId: string;
  agreementHash: string;
  replayUrl: string;
  verifyUrl: string;
  preview: string;
};

export type CallDemoNote = {
  id: string;
  at: number;
  text: string;
};

export type CallDemoPersistedState = {
  version: 1;
  demoRoomId: string;
  phase: CallDemoSyncPhase;
  agreementText: string;
  roomIds: TraversalDemoRoomIds | null;
  notes: CallDemoNote[];
  rememberedMoments: CallDemoRememberedMoment[];
  sealed: TraversalDemoSealResult | null;
  updatedAt: number;
};

export type CallDemoSyncMessage = {
  type: CallDemoSyncEventType;
  demoRoomId: string;
  instanceId: string;
  at: number;
  payload: unknown;
};

export type CallDemoSyncMode = "broadcast" | "storage" | "relay" | "single";

export type CallDemoSyncTransport = {
  mode: CallDemoSyncMode;
  relayAvailable: boolean;
  broadcastAvailable: boolean;
};

export function parseDemoRoomId(search: string): string {
  const params = new URLSearchParams(search.startsWith("?") ? search : search ? `?${search}` : "");
  const raw = (params.get("room") || "").trim();
  const id = (raw || DEMO_ROOM_DEFAULT).replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 64);
  return id || DEMO_ROOM_DEFAULT;
}

export function demoStorageKey(demoRoomId: string): string {
  return `xo-call-demo:${demoRoomId}:state`;
}

export function demoChannelName(demoRoomId: string): string {
  return `xo-call-demo:${demoRoomId}`;
}

export function buildDemoRoomPath(demoRoomId: string): string {
  const params = new URLSearchParams({ demo: "1", room: demoRoomId });
  return `/call?${params.toString()}`;
}

export function buildDemoRoomUrl(origin: string, demoRoomId: string): string {
  const base = origin.replace(/\/$/, "");
  return `${base}${buildDemoRoomPath(demoRoomId)}`;
}

/**
 * True when /call?room=… should redirect to traversal demo mode (demo=1).
 * Skips production live-call URLs that include a non-traversal call id.
 */
export function shouldNormalizeToTraversalDemoCall(search: string): boolean {
  const raw = search.startsWith("?") ? search : search ? `?${search}` : "";
  const params = new URLSearchParams(raw);
  const demo = params.get("demo");
  if (demo === "1" || demo === "true") return false;
  const room = (params.get("room") || "").trim();
  if (!room) return false;
  const call = (params.get("call") || "").trim();
  if (call && !call.startsWith("xo-traversal-call-")) return false;
  return true;
}

/** Merge demo=1 into call search while preserving other query params. */
export function normalizeTraversalDemoCallSearch(search: string): string {
  const raw = search.startsWith("?") ? search.slice(1) : search || "";
  const params = new URLSearchParams(raw);
  params.set("demo", "1");
  const q = params.toString();
  return q ? `?${q}` : "?demo=1";
}


export function createEmptyDemoState(demoRoomId: string): CallDemoPersistedState {
  return {
    version: 1,
    demoRoomId,
    phase: "ready",
    agreementText: "",
    roomIds: null,
    notes: [],
    rememberedMoments: [],
    sealed: null,
    updatedAt: Date.now(),
  };
}

export function mergeDemoState(
  prev: CallDemoPersistedState,
  patch: Partial<CallDemoPersistedState>,
): CallDemoPersistedState {
  return {
    ...prev,
    ...patch,
    notes: patch.notes ?? prev.notes,
    rememberedMoments: patch.rememberedMoments ?? prev.rememberedMoments,
    updatedAt: Date.now(),
  };
}

export function parsePersistedDemoState(raw: string | null): CallDemoPersistedState | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as CallDemoPersistedState;
    if (v?.version !== 1 || typeof v.demoRoomId !== "string") return null;
    return v;
  } catch {
    return null;
  }
}

export function loadPersistedDemoState(demoRoomId: string): CallDemoPersistedState | null {
  if (typeof localStorage === "undefined") return null;
  return parsePersistedDemoState(localStorage.getItem(demoStorageKey(demoRoomId)));
}

export function savePersistedDemoState(state: CallDemoPersistedState): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(demoStorageKey(state.demoRoomId), JSON.stringify(state));
  } catch {
    /* quota / private mode */
  }
}

export function applySyncMessage(
  state: CallDemoPersistedState,
  message: CallDemoSyncMessage,
): CallDemoPersistedState {
  const p = message.payload as Record<string, unknown>;
  switch (message.type) {
    case "room_started": {
      const roomIds = p.roomIds as TraversalDemoRoomIds;
      const agreementText = String(p.agreementText || state.agreementText || "");
      return mergeDemoState(state, {
        phase: "active",
        roomIds,
        agreementText,
        sealed: null,
      });
    }
    case "agreement_updated":
      return mergeDemoState(state, {
        phase: state.phase === "idle" || state.phase === "ready" ? "active" : state.phase,
        agreementText: String(p.text ?? ""),
      });
    case "note_added": {
      const note = p.note as CallDemoNote;
      if (!note?.id) return state;
      const notes = [...state.notes.filter((n) => n.id !== note.id), note].sort((a, b) => a.at - b.at);
      return mergeDemoState(state, { notes });
    }
    case "moment_remembered": {
      const moment = p.moment as CallDemoRememberedMoment;
      if (!moment?.id) return state;
      const rememberedMoments = [
        ...state.rememberedMoments.filter((m) => m.id !== moment.id),
        moment,
      ].sort((a, b) => a.at - b.at);
      return mergeDemoState(state, { rememberedMoments, phase: "active" });
    }
    case "continuity_sealed": {
      const sealed = p.sealed as TraversalDemoSealResult;
      const moment = p.moment as CallDemoRememberedMoment | undefined;
      const rememberedMoments = moment
        ? [...state.rememberedMoments.filter((m) => m.id !== moment.id), moment].sort((a, b) => a.at - b.at)
        : state.rememberedMoments;
      return mergeDemoState(state, {
        phase: "sealed",
        sealed,
        rememberedMoments,
        agreementText: String(p.agreementText ?? state.agreementText),
      });
    }
    case "resume_editing":
      return mergeDemoState(state, {
        phase: "active",
        sealed: null,
      });
    default:
      return state;
  }
}

export function resolveSyncTransport(relayAvailable: boolean): CallDemoSyncTransport {
  const broadcastAvailable = typeof BroadcastChannel !== "undefined";
  if (relayAvailable) {
    return { mode: "relay", relayAvailable: true, broadcastAvailable };
  }
  if (broadcastAvailable) {
    return { mode: "broadcast", relayAvailable: false, broadcastAvailable: true };
  }
  if (typeof localStorage !== "undefined") {
    return { mode: "storage", relayAvailable: false, broadcastAvailable: false };
  }
  return { mode: "single", relayAvailable: false, broadcastAvailable: false };
}

export function syncStatusLabel(transport: CallDemoSyncTransport): string {
  if (transport.mode === "relay" && transport.relayAvailable) return "Resonance active";
  if (transport.mode === "broadcast" && transport.broadcastAvailable) return "Presence aligned";
  if (transport.mode === "storage") return "Presence aligned";
  return "Witness listening";
}

export type CallDemoSyncClient = {
  instanceId: string;
  demoRoomId: string;
  readonly transport: CallDemoSyncTransport;
  getState: () => CallDemoPersistedState;
  publish: (type: CallDemoSyncEventType, payload: unknown) => void;
  close: () => void;
  refreshTransportLabel: () => CallDemoSyncTransport;
};

const RELAY_POLL_MS = 1500;

export function createCallDemoSync(
  demoRoomId: string,
  onState: (state: CallDemoPersistedState, source: "local" | "remote") => void,
  options?: { relayEnabled?: boolean; onTransportChange?: (t: CallDemoSyncTransport) => void },
): CallDemoSyncClient {
  const instanceId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `inst-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  let state = loadPersistedDemoState(demoRoomId) ?? createEmptyDemoState(demoRoomId);
  state = { ...state, demoRoomId };

  const channel =
    typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(demoChannelName(demoRoomId)) : null;

  let relayAvailable = false;
  let relayPollTimer: number | null = null;
  let relayProbeDone = false;
  const relayEnabled = options?.relayEnabled !== false;

  let transportState = resolveSyncTransport(false);

  const commit = (next: CallDemoPersistedState, source: "local" | "remote") => {
    state = next;
    savePersistedDemoState(state);
    onState(state, source);
  };

  const handleMessage = (message: CallDemoSyncMessage, source: "local" | "remote") => {
    if (message.demoRoomId !== demoRoomId) return;
    if (source === "remote" && message.instanceId === instanceId) return;
    commit(applySyncMessage(state, message), source);
  };

  const pushRelay = async (type: CallDemoSyncEventType, payload: unknown) => {
    if (!relayAvailable || !relayEnabled) return;
    const { formatRelayEvent, postRelayEvent } = await import("./callDemoRelay");
    const event = formatRelayEvent(type, instanceId, payload);
    const snap = await postRelayEvent(demoRoomId, event);
    if (snap && snap.updatedAt > state.updatedAt) {
      commit(snap, "local");
    }
  };

  const publish = (type: CallDemoSyncEventType, payload: unknown) => {
    const message: CallDemoSyncMessage = {
      type,
      demoRoomId,
      instanceId,
      at: Date.now(),
      payload,
    };
    handleMessage(message, "local");
    if (channel) {
      try {
        channel.postMessage(message);
      } catch {
        /* ignore */
      }
    }
    void pushRelay(type, payload);
  };

  if (channel) {
    channel.onmessage = (ev: MessageEvent<CallDemoSyncMessage>) => {
      const data = ev.data;
      if (!data || typeof data.type !== "string") return;
      handleMessage(data, "remote");
    };
  }

  const onStorage = (ev: StorageEvent) => {
    if (ev.key !== demoStorageKey(demoRoomId) || !ev.newValue) return;
    const parsed = parsePersistedDemoState(ev.newValue);
    if (!parsed) return;
    if (parsed.updatedAt <= state.updatedAt) return;
    commit(parsed, "remote");
  };

  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }

  const pollRelay = async () => {
    if (!relayAvailable || !relayEnabled) return;
    const { fetchRelaySnapshot, shouldApplyRelaySnapshot } = await import("./callDemoRelay");
    const remote = await fetchRelaySnapshot(demoRoomId);
    if (!remote) return;
    if (shouldApplyRelaySnapshot(state, remote, instanceId)) {
      commit(remote, "remote");
    }
  };

  const startRelayPoll = () => {
    if (typeof window === "undefined" || relayPollTimer != null || !relayAvailable) return;
    relayPollTimer = window.setInterval(() => {
      void pollRelay();
    }, RELAY_POLL_MS);
    void pollRelay();
  };

  const refreshTransportLabel = (): CallDemoSyncTransport => {
    transportState = relayProbeDone ? resolveSyncTransport(relayAvailable) : resolveSyncTransport(false);
    options?.onTransportChange?.(transportState);
    return transportState;
  };

  if (relayEnabled && typeof window !== "undefined") {
    void import("./callDemoRelay").then(({ probeRelayAvailable }) => {
      void probeRelayAvailable(demoRoomId).then((ok) => {
        relayAvailable = ok;
        relayProbeDone = true;
        refreshTransportLabel();
        if (ok) startRelayPoll();
      });
    });
  } else {
    relayProbeDone = true;
    refreshTransportLabel();
  }

  return {
    instanceId,
    demoRoomId,
    get transport() {
      return transportState;
    },
    getState: () => state,
    publish,
    refreshTransportLabel,
    close: () => {
      if (channel) channel.close();
      if (relayPollTimer != null && typeof window !== "undefined") {
        window.clearInterval(relayPollTimer);
        relayPollTimer = null;
      }
      if (typeof window !== "undefined") window.removeEventListener("storage", onStorage);
    },
  };
}
